import { useRef, useState } from "react";
import { useUpdateLocation } from "@/features/business/hooks/businessLocationQueries";
import { useUpdateBusiness } from "@/features/business/hooks/businessQueries";
import type { BusinessLocation } from "@/features/business/types/businessLocationTypes";
import type { Business } from "@/features/business/types/businessTypes";
import { getChangedFields } from "@/shared/utils/getChangedFields";
import type { Profile } from "../types/profileTypes";
import { useUpdateProfile } from "./profileQueries";

function profileValues(profile: Profile) {
  return { firstName: profile.firstName ?? "", lastName: profile.lastName ?? "" };
}

function businessValues(business: Business) {
  return {
    name: business.name,
    industry: business.industry ?? "",
    timezone: business.timezone,
    currency: business.currency,
  };
}

function locationValues(location: BusinessLocation) {
  return {
    name: location.name,
    address: location.address,
    city: location.city,
    state: location.state,
    postalCode: location.postalCode,
  };
}

interface ProfileEditorValues {
  profile: ReturnType<typeof profileValues>;
  business: ReturnType<typeof businessValues>;
  location: ReturnType<typeof locationValues>;
}

export type ProfileEditorSection = keyof ProfileEditorValues;

function normalizeValues(values: ProfileEditorValues): ProfileEditorValues {
  return {
    profile: {
      firstName: values.profile.firstName.trim(),
      lastName: values.profile.lastName.trim(),
    },
    business: {
      ...values.business,
      name: values.business.name.trim(),
    },
    location: {
      name: values.location.name.trim(),
      address: values.location.address.trim(),
      city: values.location.city.trim(),
      state: values.location.state.trim(),
      postalCode: values.location.postalCode.trim(),
    },
  };
}

export function useProfileEditor(profile: Profile, business: Business, location: BusinessLocation) {
  const updateProfile = useUpdateProfile();
  const updateBusiness = useUpdateBusiness(business.id);
  const updateLocation = useUpdateLocation(location.id);
  const [draft, setDraft] = useState<ProfileEditorValues>(() => ({
    profile: profileValues(profile),
    business: businessValues(business),
    location: locationValues(location),
  }));
  const [saved, setSaved] = useState(() => normalizeValues(draft));
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const saving = useRef(false);

  const normalized = normalizeValues(draft);
  const changes = {
    profile: getChangedFields(saved.profile, normalized.profile),
    business: getChangedFields(saved.business, normalized.business),
    location: getChangedFields(saved.location, normalized.location),
  };
  const changedSections = {
    profile: Object.keys(changes.profile).length > 0,
    business: Object.keys(changes.business).length > 0,
    location: Object.keys(changes.location).length > 0,
  };
  const changedSectionCount = Object.values(changedSections).filter(Boolean).length;
  const mutations = { profile: updateProfile, business: updateBusiness, location: updateLocation };

  function setSection<K extends ProfileEditorSection>(section: K, value: ProfileEditorValues[K]) {
    setDraft((previous) => ({ ...previous, [section]: value }));
    setIsSaved(false);
    mutations[section].reset();
  }

  function acceptSavedSection<K extends ProfileEditorSection>(section: K, value: ProfileEditorValues[K]) {
    setDraft((previous) => ({ ...previous, [section]: value }));
    setSaved((previous) => normalizeValues({ ...previous, [section]: value }));
  }

  async function saveChanges() {
    if (saving.current || changedSectionCount === 0) return;
    saving.current = true;
    setIsSaving(true);
    setIsSaved(false);

    const requests: { section: ProfileEditorSection; promise: Promise<void> }[] = [];
    if (changedSections.profile) {
      requests.push({
        section: "profile",
        promise: updateProfile.mutateAsync(changes.profile).then((result) => {
          acceptSavedSection("profile", profileValues(result));
        }),
      });
    }
    if (changedSections.business) {
      requests.push({
        section: "business",
        promise: updateBusiness.mutateAsync(changes.business).then((result) => {
          acceptSavedSection("business", businessValues(result));
        }),
      });
    }
    if (changedSections.location) {
      requests.push({
        section: "location",
        promise: updateLocation.mutateAsync(changes.location).then((result) => {
          acceptSavedSection("location", locationValues(result));
        }),
      });
    }

    // Commit successful sections independently so retries only send the remaining edits.
    // Rejections are exposed through the existing mutation errors.
    const results = await Promise.allSettled(requests.map(({ promise }) => promise));
    const failedIndex = results.findIndex((result) => result.status === "rejected");
    setIsSaved(failedIndex === -1);
    setIsSaving(false);
    saving.current = false;
    return failedIndex === -1 ? undefined : requests[failedIndex].section;
  }

  return {
    draft,
    setSection,
    saveChanges,
    changedSections,
    changedSectionCount,
    isSaving,
    isSaved,
    errors: {
      profile: updateProfile.error,
      business: updateBusiness.error,
      location: updateLocation.error,
    },
  };
}
